'use client'

import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { RouteSpinner } from '@/components/ui/route-spinner'
import { useSessionMeta, updateAppSessionState } from '@/state/session/context'

interface PublicGuardProps {
  children: ReactNode
}

export function PublicGuard({ children }: PublicGuardProps): React.JSX.Element {
  const router = useRouter()
  const {
    hydrated,
    isLoading,
    isAuthenticated,
    firstAccess,
    onboardingCompleted,
    postOnboardingRedirect,
  } = useSessionMeta()
  const onboardingRequired = isAuthenticated && (firstAccess || !onboardingCompleted)

  useEffect(() => {
    if (!hydrated || isLoading || !isAuthenticated) return

    if (onboardingRequired) {
      router.replace('/onboarding/modules')
      return
    }

    const target = postOnboardingRedirect ?? '/dashboard'
    if (postOnboardingRedirect) {
      updateAppSessionState((prev) => ({
        ...prev,
        postOnboardingRedirect: null,
      }))
    }

    router.replace(target)
  }, [
    hydrated,
    onboardingRequired,
    router,
    firstAccess,
    isAuthenticated,
    isLoading,
    onboardingCompleted,
    postOnboardingRedirect,
  ])

  if (!hydrated || isLoading || isAuthenticated) {
    return (
      <div className="mx-auto w-full max-w-md px-4 py-12">
        <RouteSpinner label="INICIANDO PROTOCOLO..." showLabel />
      </div>
    )
  }

  return <>{children}</>
}
